/**
 * temporal — typed time helpers.
 *
 * memory/feedback_temporality.md is explicit: derive temporal facts from
 * prov:atTime instants instead of storing static offsets. This module
 * is the single seam every UI + agent uses to ask "how old is this?"
 * Cuts every reader off from raw Date.now() arithmetic on attr blobs
 * so when we add as-of queries (bitemporal) the change lands in one
 * place.
 */
import type { GraphNode } from "../types/ontology";

const MS_PER_DAY = 86_400_000;
const MS_PER_HOUR = 3_600_000;
const MS_PER_MIN = 60_000;

/** Read a typed xsd:dateTime attr off a node. Returns ms epoch or null
 *  when the attr is absent / unparseable. The same parsing all helpers
 *  use, so a single bad ISO doesn't bleed into half the cockpit. */
export function parseAtTime(node: GraphNode | undefined, key: string = "atTime"): number | null {
  if (!node) return null;
  const v = node.attrs[key];
  if (typeof v !== "string" || v.length === 0) return null;
  const ms = Date.parse(v);
  return Number.isFinite(ms) ? ms : null;
}

/** Age in days from a node's prov:atTime to `now`. Falls back to the
 *  legacy `ageDays` static attr (sample-graph seed data) if no atTime
 *  is present — keeps old fixtures working without a migration sweep. */
export function ageDays(node: GraphNode | undefined, opts?: { now?: number; key?: string; legacyKey?: string }): number {
  const now = opts?.now ?? Date.now();
  const atKey = opts?.key ?? "atTime";
  const legacyKey = opts?.legacyKey ?? "ageDays";
  const t = parseAtTime(node, atKey);
  if (t !== null) {
    return Math.max(0, Math.floor((now - t) / MS_PER_DAY));
  }
  // Legacy fallback — static offset attr on seed data.
  if (node) {
    const v = node.attrs[legacyKey];
    if (typeof v === "number" && Number.isFinite(v)) return Math.max(0, Math.floor(v));
  }
  return 0;
}

/** Age in hours — used by short-lived activities (CatalogImports, render
 *  jobs, agent fires) where days isn't granular enough. */
export function ageHours(node: GraphNode | undefined, opts?: { now?: number; key?: string }): number {
  const now = opts?.now ?? Date.now();
  const t = parseAtTime(node, opts?.key ?? "atTime");
  if (t === null) return 0;
  return Math.max(0, Math.floor((now - t) / MS_PER_HOUR));
}

/** Age in minutes — for sub-hour audit / timeline grouping. */
export function ageMinutes(node: GraphNode | undefined, opts?: { now?: number; key?: string }): number {
  const now = opts?.now ?? Date.now();
  const t = parseAtTime(node, opts?.key ?? "atTime");
  if (t === null) return 0;
  return Math.max(0, Math.floor((now - t) / MS_PER_MIN));
}

/** Compact relative-time string: "just now" / "3m ago" / "2h ago" /
 *  "5d ago" / "3mo ago". One canonical formatter so every panel reads
 *  the same. */
export function relativeFromNode(node: GraphNode | undefined, opts?: { now?: number; key?: string; legacyKey?: string }): string {
  const now = opts?.now ?? Date.now();
  const t = parseAtTime(node, opts?.key ?? "atTime");
  if (t === null) {
    // Fall back to legacy ageDays integer if present.
    const days = node ? Number(node.attrs[opts?.legacyKey ?? "ageDays"] ?? 0) : 0;
    if (days <= 0) return "—";
    if (days === 1) return "1d ago";
    return `${days}d ago`;
  }
  const diff = Math.max(0, now - t);
  if (diff < 60_000) {
    const sec = Math.round(diff / 1000);
    return sec < 5 ? "just now" : `${sec}s ago`;
  }
  if (diff < MS_PER_HOUR) return `${Math.round(diff / MS_PER_MIN)}m ago`;
  if (diff < MS_PER_DAY)  return `${Math.round(diff / MS_PER_HOUR)}h ago`;
  if (diff < 30 * MS_PER_DAY) return `${Math.round(diff / MS_PER_DAY)}d ago`;
  return `${Math.round(diff / (30 * MS_PER_DAY))}mo ago`;
}

/** Compute the typed atTime an Approval was created given its legacy
 *  ageDays. Used to back-fill prov:atTime for fixtures so consumers
 *  can be migrated to the derived path without touching seed data. */
export function inferAtTimeFromAgeDays(node: GraphNode, now: number = Date.now()): string | null {
  const v = node.attrs["ageDays"];
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  return new Date(now - v * MS_PER_DAY).toISOString();
}

/** Integer days from `now` to a node's typed time:Instant (e.g.
 *  Milestone.dueOn). Falls back to the legacy static-integer offset
 *  attr (`dueIn`) when no time:Instant is present so seed fixtures
 *  keep working. Negative when the instant is in the past. */
export function daysUntil(node: GraphNode | undefined, opts?: { now?: number; key?: string; legacyKey?: string }): number {
  if (!node) return 0;
  const now = opts?.now ?? Date.now();
  const dueKey = opts?.key ?? "dueOn";
  const legacyKey = opts?.legacyKey ?? "dueIn";
  const ms = parseAtTime(node, dueKey);
  if (ms !== null) {
    return Math.round((ms - now) / MS_PER_DAY);
  }
  const v = node.attrs[legacyKey];
  if (typeof v === "number" && Number.isFinite(v)) return Math.round(v);
  return 0;
}
